import type { ReactElement } from 'react';
import { NavLink } from 'react-router-dom';
import { useInbox } from './Notifications';
import type { NotificationLinkView } from '../api/notifications';
import { useTranslation } from 'react-i18next';
import { Icon } from '../lib/icons';
import { NAV_GROUPS } from './navigation';
import { useAuth } from '../auth/AuthProvider';
import { useSidebar } from './SidebarToggle';

/**
 * Sidebar, ported from the prototype's `renderSidebar`.
 *
 * Markup and class names (`brand`, `mark`, `sub`, `nav-group`, `cap`,
 * `nav-item`, `active`, `side-foot`) are unchanged, so the vendored stylesheet
 * applies without a single CSS edit.
 *
 * One addition, invisible to a sighted mouse user: the links sit inside a named
 * `<nav>`, and `NavLink` marks the active one with `aria-current="page"`. The
 * prototype signalled the current screen with colour alone, which a screen
 * reader cannot convey.
 */
export function Sidebar(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { isOpen } = useSidebar();

  /*
   * Hiding an entry the caller cannot use is a courtesy, not a control — FRS
   * §4.1 calls navigation "a coarse gate". A user who types the URL anyway hits
   * the route guard, and the requests that screen would make are refused by the
   * server with 403 regardless.
   *
   * A group with no visible members is not rendered at all, rather than left as
   * an empty heading.
   */
  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.capability || can(item.capability)),
  })).filter((group) => group.items.length > 0);

  const badges = useInbox()?.badges ?? {};

  return (
    /*
     * `.sidebar.open` is the prototype's own rule: below 880px the panel is
     * fixed and translated off-screen, and this class slides it back. Above the
     * breakpoint the class has no effect, so nothing needs to know the width.
     */
    <aside className={isOpen ? 'sidebar open' : 'sidebar'} id="sidebar">
      <div className="brand">
        <div className="mark">
          <img src="/logo.png" alt="Boud" />
        </div>
        <div>
          <div className="sub">{t('boud_sub')}</div>
        </div>
      </div>

      <nav aria-label={t('nav_admin')}>
        {groups.map((group) => (
          <div className="nav-group" key={group.labelKey}>
            <div className="cap">{t(group.labelKey)}</div>
            {group.items.map((item) => {
              /*
               * BR-067 — the badge counts *unread notifications addressed to me
               * for this screen*, never records in a given state. The prototype
               * comment at `unreadFor` says the same thing, and the distinction
               * matters: a requester's badge rises when their own request is
               * resolved, which a status count could never express.
               */
              const badge = badges[item.path.slice(1) as NotificationLinkView] ?? 0;

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                >
                  <Icon name={item.icon} />
                  <span>{t(item.key)}</span>
                  {badge > 0 ? (
                    <span className="nav-badge" aria-label={t('nt_unread', { count: badge })}>
                      {badge}
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>
    </aside>
  );
}
